import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface FileSystemItem {
    id: string;
    name: string;
    type: "file" | "folder";
    path: string;
    isQuickAccess?: boolean;
    parentId?: string;
    children?: string[]; // Array of IDs
    createdAt: Date;
    modifiedAt: Date;
    size?: number;
    icon?: string;
}

interface DirectoryState {
    items: { [key: string]: FileSystemItem };
    currentPath: string[];
    selectedItems: string[];
    quickAccessItems: string[];
    expandedFolders: string[];
    isLoading: boolean;
    error: string | null;
}

const initialState: DirectoryState = {
    items: {},
    currentPath: [],
    selectedItems: [],
    quickAccessItems: [],
    expandedFolders: [],
    isLoading: false,
    error: null,
};

const directorySlice = createSlice({
    name: "directory",
    initialState,
    reducers: {
        addItem: (state, action: PayloadAction<FileSystemItem>) => {
            const item = action.payload;
            state.items[item.id] = item;

            if (item.parentId && state.items[item.parentId]) {
                state.items[item.parentId].children = state.items[item.parentId].children || [];
                if (!state.items[item.parentId].children?.includes(item.id)) {
                    state.items[item.parentId].children?.push(item.id);
                }
            }
        },
        removeItem: (state, action: PayloadAction<string>) => {
            const itemId = action.payload;
            const item = state.items[itemId];

            if (item.parentId && state.items[item.parentId]?.children) {
                state.items[item.parentId].children = state.items[item.parentId].children?.filter((id) => id !== itemId);
            }

            delete state.items[itemId];
        },
        setCurrentPath: (state, action: PayloadAction<string[]>) => {
            state.currentPath = action.payload;
        },
        toggleQuickAccess: (state, action: PayloadAction<string>) => {
            const itemId = action.payload;
            if (state.quickAccessItems.includes(itemId)) {
                state.quickAccessItems = state.quickAccessItems.filter((id) => id !== itemId);
                if (state.items[itemId]) {
                    state.items[itemId].isQuickAccess = false;
                }
            } else {
                state.quickAccessItems.push(itemId);
                if (state.items[itemId]) {
                    state.items[itemId].isQuickAccess = true;
                }
            }
        },
        toggleFolderExpand: (state, action: PayloadAction<string>) => {
            const folderId = action.payload;
            if (state.expandedFolders.includes(folderId)) {
                state.expandedFolders = state.expandedFolders.filter((id) => id !== folderId);
            } else {
                state.expandedFolders.push(folderId);
            }
        },
        setSelectedItems: (state, action: PayloadAction<string[]>) => {
            state.selectedItems = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        navigateToPathSegment: (state, action: PayloadAction<number>) => {
            state.currentPath = state.currentPath.slice(0, action.payload + 1);
        },
    },
});

export const {
    addItem,
    removeItem,
    setCurrentPath,
    toggleQuickAccess,
    toggleFolderExpand,
    setSelectedItems,
    setError,
    setLoading,
    navigateToPathSegment,
} = directorySlice.actions;

export const directoryReducer = directorySlice.reducer;
